import React, { useState, useEffect } from 'react';
import { 
  PlayCircle, RefreshCw, AlertCircle, DollarSign,
  BarChart3, TrendingUp, XCircle, CheckCircle, Percent,
  Users, Camera, Clock, MousePointer
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import api from '@/lib/api';

// Stat card component
const StatCard = ({ title, value, icon: Icon, description, color, suffix = '', trend = null }) => (
  <Card className="border-border bg-card">
    <CardHeader className="flex flex-row items-center justify-between pb-2">
      <CardTitle className="text-sm font-medium text-muted-foreground">
        {title}
      </CardTitle>
      <div className={`flex h-10 w-10 items-center justify-center rounded-lg ${color}`}>
        <Icon className="h-5 w-5" />
      </div>
    </CardHeader>
    <CardContent>
      <div className="text-3xl font-bold tracking-tight">
        {value}{suffix}
      </div>
      {description && (
        <p className="mt-1 text-xs text-muted-foreground">{description}</p>
      )}
      {trend !== null && (
        <p className={`mt-1 text-xs ${trend >= 0 ? 'text-green-400' : 'text-red-400'}`}>
          {trend >= 0 ? '↑' : '↓'} {Math.abs(trend)}% vs previous period
        </p>
      )}
    </CardContent>
  </Card>
);

// Format percentage
const formatPercent = (value) => {
  if (value == null || isNaN(value)) return '0.0';
  return (value * 100).toFixed(1);
};

// Format large numbers
const formatNumber = (num) => {
  if (num == null) return '0';
  if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
  if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
  return num.toLocaleString();
};

export function Ads() {
  const [summary, setSummary] = useState(null);
  const [recentEvents, setRecentEvents] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [timeRange, setTimeRange] = useState('7d');
  const [adsEnabled, setAdsEnabled] = useState(false);

  const fetchAdsSummary = async () => {
    setIsLoading(true);
    setError(null);
    try {
      // Check if ads are enabled first
      const settings = await api.getSettings();
      setAdsEnabled(settings?.ads_enabled ?? false);
      
      // Always try to fetch data (for testing even when disabled)
      try {
        const [summaryData, eventsData] = await Promise.all([
          api.getAdsSummary(timeRange),
          api.getAdsEvents({ limit: 20 })
        ]);
        setSummary(summaryData);
        setRecentEvents(eventsData.events || eventsData || []);
      } catch (e) {
        // If endpoints don't exist yet, just show placeholder
        if (!e.message?.includes('404')) {
          console.log('Ad data not available yet');
        }
      }
    } catch (err) {
      setError(err.message || 'Failed to load ads data');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchAdsSummary();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timeRange]);

  // Calculate derived metrics
  const fillRate = summary?.total_requests > 0 
    ? summary.total_fills / summary.total_requests 
    : 0;
  const completionRate = summary?.total_impressions > 0 
    ? summary.total_completes / summary.total_impressions 
    : 0;
  const errorRate = summary?.total_requests > 0 
    ? (summary.total_errors || 0) / summary.total_requests 
    : 0;

  // Loading state
  if (isLoading) {
    return (
      <div className="space-y-6" data-testid="ads-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Ads Analytics</h1>
            <p className="text-muted-foreground">Track ad performance and revenue</p>
          </div>
        </div>
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4">
          {[1, 2, 3, 4].map(i => (
            <Card key={i} className="border-border bg-card">
              <CardContent className="p-6">
                <div className="h-20 animate-pulse rounded bg-muted" />
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  // Check if we have any data
  const hasData = summary && (summary.total_requests > 0 || summary.total_impressions > 0);

  // Placeholder state when no data
  if (!hasData) {
    return (
      <div className="space-y-6" data-testid="ads-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Ads Analytics</h1>
            <p className="text-muted-foreground">Track ad performance and revenue</p>
          </div>
          <div className="flex items-center gap-2">
            {!adsEnabled && (
              <Badge variant="secondary" className="text-sm">
                Ads Disabled
              </Badge>
            )}
            <Button variant="outline" size="sm" onClick={fetchAdsSummary}>
              <RefreshCw className="mr-2 h-4 w-4" />
              Refresh
            </Button>
          </div>
        </div>

        {/* Placeholder Stats */}
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4 opacity-50">
          <StatCard
            title="Total Requests"
            value="—"
            icon={PlayCircle}
            description="VAST ad requests"
            color="bg-blue-500/10 text-blue-500"
          />
          <StatCard
            title="Fill Rate"
            value="—"
            icon={TrendingUp}
            description="Ads returned / requested"
            color="bg-green-500/10 text-green-500"
            suffix="%"
          />
          <StatCard
            title="Impressions"
            value="—"
            icon={CheckCircle}
            description="Ads started playing"
            color="bg-purple-500/10 text-purple-500"
          />
          <StatCard
            title="Completion Rate"
            value="—"
            icon={BarChart3}
            description="Ads watched to end"
            color="bg-orange-500/10 text-orange-500"
            suffix="%"
          />
        </div>

        {/* Coming Soon / Waiting Card */}
        <Card className="border-border bg-card">
          <CardContent className="flex flex-col items-center justify-center py-16">
            <div className="flex h-16 w-16 items-center justify-center rounded-full bg-primary/10 mb-4">
              <DollarSign className="h-8 w-8 text-primary" />
            </div>
            <h2 className="text-xl font-semibold mb-2">
              {adsEnabled ? 'Waiting for Ad Data' : 'Ad Analytics Ready'}
            </h2>
            <p className="text-muted-foreground text-center max-w-md">
              {adsEnabled 
                ? 'Ad events will appear here as users view streams with VAST tags configured.'
                : 'Enable ads in Settings and configure VAST tags on cameras to start tracking.'}
            </p>
            <div className="mt-6 flex flex-wrap gap-3 justify-center">
              <Badge variant="outline" className="text-sm">
                <PlayCircle className="mr-1 h-3 w-3" />
                Preroll Ads
              </Badge>
              <Badge variant="outline" className="text-sm">
                <Clock className="mr-1 h-3 w-3" />
                Midroll (Still Watching)
              </Badge>
              <Badge variant="outline" className="text-sm">
                <Users className="mr-1 h-3 w-3" />
                By User Tier
              </Badge>
              <Badge variant="outline" className="text-sm">
                <Camera className="mr-1 h-3 w-3" />
                By Camera
              </Badge>
            </div>
          </CardContent>
        </Card>

        {/* Event Types Reference */}
        <Card className="border-border bg-card">
          <CardHeader>
            <CardTitle>Event Types Tracked</CardTitle>
            <CardDescription>
              Events reported by mobile apps during ad playback
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
              <div className="space-y-2">
                <p className="font-medium text-foreground">Ad Lifecycle</p>
                <ul className="text-muted-foreground space-y-1">
                  <li>• <code className="text-xs bg-secondary px-1 rounded">request</code> - VAST fetch started</li>
                  <li>• <code className="text-xs bg-secondary px-1 rounded">fill</code> - Ad URL received</li>
                  <li>• <code className="text-xs bg-secondary px-1 rounded">no_fill</code> - No ad returned</li>
                  <li>• <code className="text-xs bg-secondary px-1 rounded">impression</code> - Ad started</li>
                  <li>• <code className="text-xs bg-secondary px-1 rounded">complete</code> - Ad finished</li>
                </ul>
              </div>
              <div className="space-y-2">
                <p className="font-medium text-foreground">Progress & Interaction</p>
                <ul className="text-muted-foreground space-y-1">
                  <li>• <code className="text-xs bg-secondary px-1 rounded">progress_25</code> - 25% watched</li>
                  <li>• <code className="text-xs bg-secondary px-1 rounded">progress_50</code> - 50% watched</li>
                  <li>• <code className="text-xs bg-secondary px-1 rounded">progress_75</code> - 75% watched</li>
                  <li>• <code className="text-xs bg-secondary px-1 rounded">skip</code> - User skipped</li>
                  <li>• <code className="text-xs bg-secondary px-1 rounded">click</code> - User tapped ad</li>
                </ul>
              </div>
              <div className="space-y-2">
                <p className="font-medium text-foreground">Still Watching</p>
                <ul className="text-muted-foreground space-y-1">
                  <li>• <code className="text-xs bg-secondary px-1 rounded">still_watching_shown</code> - Prompt displayed</li>
                  <li>• <code className="text-xs bg-secondary px-1 rounded">still_watching_continue</code> - User continued</li>
                  <li>• <code className="text-xs bg-secondary px-1 rounded">still_watching_exit</code> - User exited</li>
                  <li>• <code className="text-xs bg-secondary px-1 rounded">error</code> - Playback error</li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Active ads state with data
  return (
    <div className="space-y-6" data-testid="ads-page">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Ads Analytics</h1>
          <p className="text-muted-foreground">Track ad performance and revenue</p>
        </div>
        <div className="flex items-center gap-3">
          {adsEnabled ? (
            <Badge className="bg-green-500/20 text-green-400">Ads Enabled</Badge>
          ) : (
            <Badge variant="secondary">Testing Mode</Badge>
          )}
          <Select value={timeRange} onValueChange={setTimeRange}>
            <SelectTrigger className="w-28">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="24h">24 Hours</SelectItem>
              <SelectItem value="7d">7 Days</SelectItem>
              <SelectItem value="30d">30 Days</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" size="sm" onClick={fetchAdsSummary}>
            <RefreshCw className="mr-2 h-4 w-4" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Main Stats */}
      <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4">
        <StatCard
          title="Total Requests"
          value={formatNumber(summary?.total_requests || 0)}
          icon={PlayCircle}
          description="VAST ad requests"
          color="bg-blue-500/10 text-blue-500"
        />
        <StatCard
          title="Fill Rate"
          value={formatPercent(fillRate)}
          icon={TrendingUp}
          description={`${formatNumber(summary?.total_fills || 0)} fills`}
          color={fillRate >= 0.7 ? "bg-green-500/10 text-green-500" : "bg-yellow-500/10 text-yellow-500"}
          suffix="%"
        />
        <StatCard
          title="Impressions"
          value={formatNumber(summary?.total_impressions || 0)}
          icon={CheckCircle}
          description="Ads started playing"
          color="bg-purple-500/10 text-purple-500"
        />
        <StatCard
          title="Completion Rate"
          value={formatPercent(completionRate)}
          icon={BarChart3}
          description={`${formatNumber(summary?.total_completes || 0)} completes`}
          color={completionRate >= 0.7 ? "bg-green-500/10 text-green-500" : "bg-orange-500/10 text-orange-500"}
          suffix="%"
        />
      </div>

      {/* Secondary Stats */}
      <div className="grid grid-cols-1 gap-6 md:grid-cols-4">
        <StatCard
          title="Skips"
          value={formatNumber(summary?.total_skips || 0)}
          icon={XCircle}
          description="Users skipped ad"
          color="bg-yellow-500/10 text-yellow-500"
        />
        <StatCard
          title="Clicks"
          value={formatNumber(summary?.total_clicks || 0)}
          icon={MousePointer}
          description="Ad interactions"
          color="bg-blue-500/10 text-blue-500"
        />
        <StatCard
          title="Errors"
          value={formatNumber(summary?.total_errors || 0)}
          icon={AlertCircle}
          description={`${formatPercent(errorRate)}% error rate`}
          color={errorRate > 0.1 ? "bg-red-500/10 text-red-500" : "bg-gray-500/10 text-gray-500"}
        />
        <StatCard
          title="No Fill"
          value={formatNumber(summary?.total_no_fills || 0)}
          icon={XCircle}
          description="No ad returned"
          color="bg-gray-500/10 text-gray-500"
        />
      </div>

      {/* Breakdowns */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        {/* By User Tier */}
        <Card className="border-border bg-card">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Users className="h-5 w-5 text-primary" />
              By User Tier
            </CardTitle>
          </CardHeader>
          <CardContent>
            {summary?.by_tier && Object.keys(summary.by_tier).length > 0 ? (
              <div className="space-y-3">
                {Object.entries(summary.by_tier)
                  .sort(([,a], [,b]) => b - a)
                  .map(([tier, count]) => (
                    <div key={tier} className="flex items-center justify-between">
                      <span className="capitalize">{tier}</span>
                      <Badge variant="secondary">{formatNumber(count)}</Badge>
                    </div>
                  ))}
              </div>
            ) : (
              <p className="text-muted-foreground text-sm">No tier data yet</p>
            )}
          </CardContent>
        </Card>

        {/* By Ad Context */}
        <Card className="border-border bg-card">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Clock className="h-5 w-5 text-primary" />
              By Ad Context
            </CardTitle>
          </CardHeader>
          <CardContent>
            {summary?.by_context && Object.keys(summary.by_context).length > 0 ? (
              <div className="space-y-3">
                {Object.entries(summary.by_context)
                  .sort(([,a], [,b]) => b - a)
                  .map(([context, count]) => (
                    <div key={context} className="flex items-center justify-between">
                      <span className="capitalize">{context.replace(/_/g, ' ')}</span>
                      <Badge variant="secondary">{formatNumber(count)}</Badge>
                    </div>
                  ))}
              </div>
            ) : (
              <p className="text-muted-foreground text-sm">No context data yet</p>
            )}
          </CardContent>
        </Card>

        {/* By Camera */}
        <Card className="border-border bg-card">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Camera className="h-5 w-5 text-primary" />
              Top Cameras
            </CardTitle>
          </CardHeader>
          <CardContent>
            {summary?.by_camera && Object.keys(summary.by_camera).length > 0 ? (
              <div className="space-y-3">
                {Object.entries(summary.by_camera)
                  .sort(([,a], [,b]) => b - a)
                  .slice(0, 8)
                  .map(([camera, count]) => (
                    <div key={camera} className="flex items-center justify-between">
                      <span className="truncate flex-1 mr-2">{camera}</span>
                      <Badge variant="secondary">{formatNumber(count)}</Badge>
                    </div>
                  ))}
              </div>
            ) : (
              <p className="text-muted-foreground text-sm">No camera data yet</p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Recent Events */}
      {recentEvents.length > 0 && (
        <Card className="border-border bg-card">
          <CardHeader>
            <CardTitle className="text-lg">Recent Ad Events</CardTitle>
            <CardDescription>Last 20 ad events across all users</CardDescription>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Event</TableHead>
                  <TableHead>User</TableHead>
                  <TableHead>Camera</TableHead>
                  <TableHead>Context</TableHead>
                  <TableHead>Platform</TableHead>
                  <TableHead>Time</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {recentEvents.map((event, i) => (
                  <TableRow key={event._id || i}>
                    <TableCell>
                      <Badge 
                        variant="outline" 
                        className={`text-xs ${
                          event.event_type === 'complete' ? 'border-green-500 text-green-500' :
                          event.event_type === 'impression' ? 'border-purple-500 text-purple-500' :
                          event.event_type === 'error' ? 'border-red-500 text-red-500' :
                          event.event_type === 'skip' ? 'border-yellow-500 text-yellow-500' :
                          ''
                        }`}
                      >
                        {event.event_type}
                      </Badge>
                    </TableCell>
                    <TableCell className="font-mono text-xs">
                      {event.user_id || 'anonymous'}
                    </TableCell>
                    <TableCell className="truncate max-w-[150px]">
                      {event.camera_id}
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {event.ad_context || '—'}
                    </TableCell>
                    <TableCell className="text-xs">
                      {event.platform}
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {event.timestamp ? new Date(event.timestamp).toLocaleTimeString() : '—'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

export default Ads;
